import { AnimatePresence, motion } from 'framer-motion';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { activateOnKeyDown } from '../helpers';
import { ChevronDownIcon, ChevronRightIcon, StarsIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Count } from './Count';
import { FlexGap } from './FlexGap';
import { IconWrapper } from './Icon';
import { MarkdownStyled } from './MarkdownStyled';
import { Heading3 } from './Text';

const Container = styled.div`
	box-sizing: border-box;
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.control}px;
	background: ${tokens.brand.background};
	border: 1px solid ${tokens.brand.border};
`;

const Header = styled(FlexGap)<{ $clickable?: boolean }>`
	align-items: center;
	width: 100%;
	min-width: 0;
	${({ $clickable }) => $clickable && `cursor: pointer;`}
`;

const Body = styled.div`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${tokens.typography.lineHeights.body};
	letter-spacing: 0.01em;
`;

const TruncatedContainer = styled.div<{ $maxHeight: number }>`
	position: relative;
	overflow: hidden;
	width: 100%;
	max-height: ${({ $maxHeight }) => $maxHeight}px;
	transition: max-height 0.3s ease-in-out;
`;

const InnerContent = styled.div`
	width: 100%;
`;

const Fadeout = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 48px;
	pointer-events: none;
	background: linear-gradient(to bottom, transparent, ${tokens.brand.background});
`;

const ToggleButton = styled.button`
	padding: ${tokens.gap.inline}px;
	background: transparent;
	border: none;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
`;

const ChevronWrapper = styled(IconWrapper)<{ $expanded: boolean }>`
	transition: transform 0.2s ease-in-out;
	transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
`;

interface AICardBaseProps {
	variant?: 'default' | 'extended';
	title: React.ReactNode;
	icon?: React.ReactNode;
	actions?: React.ReactNode;
	children: React.ReactNode;
	className?: string;
	collapsed?: boolean;
	onHeaderClick?: () => void;
	count?: number;
	/**
	 * When true, styles the card's children as rendered Markdown
	 * (restores list bullets, adds spacing around bold-only paragraphs, etc.).
	 */
	markdown?: boolean;
}

/**
 * When `maxHeight` is set, the content area is clamped to that pixel height
 * and expand/collapse labels must be provided (translated by the caller).
 */
export interface AICardWithMaxHeightProps extends AICardBaseProps {
	maxHeight: number;
	expandLabel: string;
	collapseLabel: string;
}

interface AICardWithoutMaxHeightProps extends AICardBaseProps {
	maxHeight?: undefined;
	expandLabel?: never;
	collapseLabel?: never;
}

export type AICardProps = AICardWithMaxHeightProps | AICardWithoutMaxHeightProps;

export const AICard = ({
	title,
	icon,
	actions,
	children,
	className,
	collapsed,
	onHeaderClick,
	maxHeight,
	expandLabel,
	collapseLabel,
	markdown,
	count,
}: AICardProps) => {
	const [expanded, setExpanded] = useState(false);
	const [contentHeight, setContentHeight] = useState<number | null>(null);
	const innerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!maxHeight || !innerRef.current) return;
		const el = innerRef.current;
		const observer = new ResizeObserver(() => {
			setContentHeight(el.getBoundingClientRect().height);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [maxHeight]);

	const overflowing = maxHeight != null && contentHeight != null && contentHeight > maxHeight;
	const effectiveMaxHeight =
		expanded || !overflowing ? (contentHeight ?? 100000) : (maxHeight ?? 100000);

	const wrappedChildren = markdown ? <MarkdownStyled>{children}</MarkdownStyled> : children;

	function handleHeaderKeyDown(event: KeyboardEvent<HTMLDivElement>) {
		if (event.target !== event.currentTarget) {
			return;
		}

		activateOnKeyDown(event, () => event.currentTarget.click());
	}

	return (
		<Container className={className}>
			<FlexGap flexDirection="column" gap={collapsed ? 0 : tokens.gap.list}>
				<Header
					gap={tokens.gap.element}
					justifyContent="space-between"
					$clickable={!!onHeaderClick}
					role={onHeaderClick ? 'button' : undefined}
					tabIndex={onHeaderClick ? 0 : undefined}
					aria-expanded={onHeaderClick ? !collapsed : undefined}
					onClick={onHeaderClick}
					onKeyDown={onHeaderClick ? handleHeaderKeyDown : undefined}
				>
					<FlexGap alignItems="center" gap={tokens.gap.element} minWidth={0}>
						<FlexGap alignItems="center" gap={tokens.gap.inline} minWidth={0}>
							{onHeaderClick && (
								<IconWrapper size={16} color={tokens.icon.primary} flexShrink={0}>
									{collapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
								</IconWrapper>
							)}
							<IconWrapper size={20} color={tokens.brand.icon} flexShrink={0}>
								{icon ?? <StarsIcon color={tokens.brand.icon} />}
							</IconWrapper>
							{typeof title === 'string' ? <Heading3>{title}</Heading3> : title}
						</FlexGap>
						{count != null && <Count count={count} variant="brand" />}
					</FlexGap>
					{actions}
				</Header>
				<AnimatePresence>
					<motion.div
						initial={false}
						animate={{ height: collapsed ? 0 : 'auto', opacity: collapsed ? 0 : 1 }}
						transition={{ duration: 0.2, ease: 'easeInOut' }}
						style={{ overflow: 'hidden' }}
					>
						<Body>
							{maxHeight ? (
								<>
									<TruncatedContainer $maxHeight={effectiveMaxHeight}>
										<InnerContent ref={innerRef}>{wrappedChildren}</InnerContent>
										{overflowing && !expanded && <Fadeout />}
									</TruncatedContainer>
									{overflowing && (
										<FlexGap justifyContent="center" mt={2}>
											<ToggleButton
												type="button"
												aria-label={expanded ? collapseLabel : expandLabel}
												onClick={() => setExpanded((e) => !e)}
											>
												<ChevronWrapper size={20} color={tokens.icon.primary} $expanded={expanded}>
													<ChevronDownIcon />
												</ChevronWrapper>
											</ToggleButton>
										</FlexGap>
									)}
								</>
							) : (
								wrappedChildren
							)}
						</Body>
					</motion.div>
				</AnimatePresence>
			</FlexGap>
		</Container>
	);
};
